/**
 * Who is signed in, as one store the whole app reads.
 *
 * Three states, not two. "Loading" is not "signed out": the session is only
 * knowable after a round trip, and a gate that treats the gap as signed-out
 * flashes the front door at every returning user on every cold launch. So the
 * status starts at `loading` and the gate renders neither side until it moves.
 *
 * The round trip happens exactly once, at boot. `useAuthSession` is a subscriber
 * to that one result, not a fetch of its own — a hook that refetched per mount
 * would hit `/get-session` on every route change, which on a phone at a table is
 * a spinner between screens.
 *
 * Two things can end a session and both land here:
 *
 *   - `signOut()`, which the user asked for.
 *   - A 401 from the sync transport, which nobody asked for. `sync.ts` pauses
 *     the outbox on that (keeping the queued writes) and announces it through
 *     `subscribeAuthLost`; this module turns the announcement into the signed-out
 *     state so the user is asked to sign in again instead of watching their
 *     ratings quietly fail to reach the table.
 */

import { useSyncExternalStore } from "react";

import { idbAvailable } from "../store/idb.ts";
import { adoptAccount, retrySync, startSync, subscribeAuthLost } from "../store/sync.ts";
import { authClient } from "./client.ts";

export type AuthStatus = "loading" | "signed-in" | "signed-out";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
}

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** Why the session ended, when it ended on its own. Shown on the front door. */
  notice: string | null;
}

/** What an action tells its caller. Never a status code — the form shows prose. */
export type AuthResult = { ok: true } | { ok: false; message: string };

const LOADING: AuthState = { status: "loading", user: null, notice: null };

let state: AuthState = LOADING;
const listeners = new Set<(s: AuthState) => void>();

export function getAuthState(): AuthState {
  return state;
}

export function subscribeAuth(fn: (s: AuthState) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function setState(next: AuthState): void {
  state = next;
  for (const fn of listeners) fn(state);
}

/**
 * Reset the store, optionally to a chosen state. Tests only — the app has
 * exactly one session per page load, and `resolveSession` is deliberately not
 * re-runnable without this.
 */
export function resetAuthForTests(seed: AuthState = LOADING): void {
  state = seed;
  resolving = null;
  listeners.clear();
}

// ---------------------------------------------------------------------------
// Reading the session
// ---------------------------------------------------------------------------

/** The wire shape as this module treats it: nothing trusted until checked. */
function readUser(data: unknown): AuthUser | null {
  if (!data || typeof data !== "object") return null;
  const user = (data as { user?: unknown }).user;
  if (!user || typeof user !== "object") return null;
  const { id, name, email } = user as Record<string, unknown>;
  if (typeof id !== "string" || id === "") return null;
  return {
    id,
    name: typeof name === "string" && name !== "" ? name : "Player",
    email: typeof email === "string" ? email : "",
  };
}

let resolving: Promise<AuthState> | null = null;

/**
 * Ask the server who we are. Idempotent: the first call owns the request and
 * every later one gets the same promise, so a double-invoked effect or a second
 * entry point cannot turn one session read into two.
 */
export function resolveSession(): Promise<AuthState> {
  if (resolving) return resolving;
  resolving = (async () => {
    let user: AuthUser | null = null;
    try {
      const { data } = await authClient.getSession();
      user = readUser(data);
    } catch {
      // The network is down, or a service worker answered with something that
      // is not JSON. Either way there is no proven session, and the front door
      // is the honest thing to show — it is also the only screen that works
      // without the server.
      user = null;
    }
    if (user) {
      await enterAccount(user);
      setState({ status: "signed-in", user, notice: null });
    } else {
      setState({ status: "signed-out", user: null, notice: null });
    }
    return state;
  })();
  return resolving;
}

/**
 * Everything that has to happen before a signed-in user sees their library:
 * bind the local store to the account (wiping it if this browser last held a
 * different one), then start the sync loop and lift any pause a previous 401
 * left behind so queued writes go out.
 */
async function enterAccount(user: AuthUser): Promise<void> {
  // Everything below is IndexedDB-backed and no-ops without a store, so the
  // whole block is gated on one question rather than four early returns
  // downstream. It is also what lets this module be exercised outside a browser.
  if (!idbAvailable()) return;
  try {
    await adoptAccount(user.id);
  } catch {
    // A failed adoption must not cost the user their sign-in. The worst case is
    // a stale cursor, which the next successful sign-in repairs.
  }
  startSync();
  void retrySync();
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Better Auth's error codes, said the way someone standing at a table would.
 *
 * The codes are matched first and the server's own message is the fallback,
 * because a message is at least true even when it is written for a developer.
 * "Something went wrong" is the last resort, never the first answer.
 */
export function describeAuthError(error: unknown): string {
  const code = typeof (error as { code?: unknown })?.code === "string"
    ? ((error as { code: string }).code)
    : "";
  const message = typeof (error as { message?: unknown })?.message === "string"
    ? ((error as { message: string }).message)
    : "";

  switch (code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return "That email and password do not match an account.";
    case "USER_ALREADY_EXISTS":
    case "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL":
      return "There is already an account with that email. Sign in instead.";
    case "PASSWORD_TOO_SHORT":
      return "Passwords need at least 8 characters.";
    case "PASSWORD_TOO_LONG":
      return "That password is too long.";
    case "INVALID_EMAIL":
      return "That does not look like an email address.";
    case "FAILED_TO_CREATE_USER":
      return "The account could not be created. Try again in a moment.";
    default:
      break;
  }
  if (message) return message;
  return "Something went wrong at our end. Nothing you typed was lost — try again.";
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export interface Credentials {
  email: string;
  password: string;
}

export interface Registration extends Credentials {
  name: string;
}

/**
 * Shared tail of sign-in and sign-up: both endpoints auto-sign-in on success and
 * both answer with `{ data, error }` rather than throwing, so a rejected promise
 * here means the network, not the credentials.
 */
async function completeSignIn(
  call: () => Promise<{ data: unknown; error: unknown }>,
): Promise<AuthResult> {
  let answer: { data: unknown; error: unknown };
  try {
    answer = await call();
  } catch {
    return { ok: false, message: "Could not reach Punchboard. Check the connection and try again." };
  }
  if (answer.error) return { ok: false, message: describeAuthError(answer.error) };

  const user = readUser(answer.data);
  if (!user) {
    // A 200 with no user in it: a captive portal, or a stale service worker
    // answering for the endpoint. Re-reading the session is cheap and settles it.
    const { data } = await authClient.getSession().catch(() => ({ data: null }));
    const confirmed = readUser(data);
    if (!confirmed) {
      return { ok: false, message: "Signed in, but the session did not stick. Try again." };
    }
    await enterAccount(confirmed);
    setState({ status: "signed-in", user: confirmed, notice: null });
    return { ok: true };
  }

  await enterAccount(user);
  setState({ status: "signed-in", user, notice: null });
  return { ok: true };
}

export function signIn(credentials: Credentials): Promise<AuthResult> {
  return completeSignIn(() => authClient.signIn.email(credentials));
}

export function signUp(registration: Registration): Promise<AuthResult> {
  return completeSignIn(() => authClient.signUp.email(registration));
}

/**
 * End the session on the server, then locally whatever the server said.
 *
 * The local store is deliberately left alone: unsynced work belongs to the
 * account that queued it and signing out is not "forget this device". It is
 * cleared only when a *different* account signs in here (see `adoptAccount`),
 * or from the explicit wipe on the settings screen.
 */
export async function signOut(): Promise<AuthResult> {
  try {
    await authClient.signOut();
  } catch {
    // The cookie may well be gone from the browser already; refusing to move to
    // the front door because the network hiccuped would strand the user signed
    // in to a session they cannot use.
  }
  setState({ status: "signed-out", user: null, notice: null });
  return { ok: true };
}

// ---------------------------------------------------------------------------
// The 401 that nobody asked for
// ---------------------------------------------------------------------------

const EXPIRED_NOTICE = "Your sign-in expired. Sign in again — nothing you rated has been lost.";

/**
 * Bound once, at module load, so it is live before the first sync attempt.
 * Only a signed-in session can be lost, so a 401 arriving while the front door
 * is already up changes nothing.
 */
subscribeAuthLost(() => {
  if (state.status !== "signed-in") return;
  setState({ status: "signed-out", user: null, notice: EXPIRED_NOTICE });
});

// ---------------------------------------------------------------------------
// React
// ---------------------------------------------------------------------------

export function useAuthSession(): AuthState {
  return useSyncExternalStore(
    (fn) => subscribeAuth(() => fn()),
    getAuthState,
    // Server render (tests use one) sees the pre-boot state, which is the truth
    // before the round trip has happened.
    () => state,
  );
}
